"use client";

import React from "react";
import { Activity, CircleAlert as AlertCircle, Compass, Footprints, Info, Scale, ShieldAlert, Sparkles } from 'lucide-react';
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface AssessmentItem {
  id: string;
  name: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
}
const assessmentItems: AssessmentItem[] = [{
  id: "gait",
  name: "Gait",
  icon: Footprints
}, {
  id: "plantar-pressure",
  name: "Plantar pressure distribution",
  icon: Activity
}, {
  id: "left-right-loading",
  name: "Left-right loading",
  icon: Scale
}, {
  id: "balance",
  name: "Balance",
  icon: Compass
}, {
  id: "centre-of-pressure",
  name: "Centre of pressure movement",
  icon: Sparkles
}, {
  id: "movement-symptoms",
  name: "Movement-related symptoms",
  icon: AlertCircle
}, {
  id: "functional-limitations",
  name: "Functional limitations",
  icon: ShieldAlert
}];
export default function MovementAssessment() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15
  });
  return <section id="functionalmovementassessment" data-nav="light" ref={ref} className="relative bg-background text-foreground py-16 md:py-24 overflow-hidden border-t border-border">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          {/* Header Block */}
          <div className="max-w-3xl">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground">
              Fitness is more than cardiovascular capacity
            </h2>
            <p className="mt-4 text-lg sm:text-xl text-muted-foreground leading-relaxed">
              Remaining physically capable also depends on how well we move, load, balance and respond to everyday physical demands.
            </p>
          </div>

          {/* Assessment Parameters Section */}
          <div className="mt-12">
            <div className="border-b border-border pb-4 mb-8">
              <h3 className="text-base sm:text-lg font-medium text-foreground">
                PhysAnalytica can assess:
              </h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {assessmentItems.map((item, index) => {
              const IconComponent = item.icon;
              return <div key={item.id} data-index={index}>
                    <Card className="h-full border border-border/80 bg-card hover:border-primary/40 transition-colors shadow-xs">
                      <CardHeader className="flex flex-row items-center gap-3 p-5">
                        <div className="p-2 rounded-md bg-secondary text-primary shrink-0">
                          <IconComponent className="size-5" />
                        </div>
                        <CardTitle className="text-base font-medium leading-snug">
                          {item.name}
                        </CardTitle>
                      </CardHeader>
                    </Card>
                  </div>;
            })}
            </div>
          </div>

          {/* Informative Limitation Statement */}
          <div className="mt-10 max-w-4xl">
            <Alert className="border-border bg-muted/50 text-foreground">
              <Info className="size-5 text-muted-foreground" />
              <AlertDescription className="text-sm sm:text-base text-muted-foreground">
                These measurements provide information about current performance during the assessed tasks. They are not diagnostic or predictive of lifespan by themselves.
              </AlertDescription>
            </Alert>
          </div>
        </div>
      </div>
    </section>;
}